import clevertapTracking from "clevertapTracking";
import { getOrderInternalConsult_getOrderInternal } from "graphql/types/getOrderInternalConsult";
import { getOrderInternalDiagnostic_getOrderInternal } from "graphql/types/getOrderInternalDiagnostic";
import { getOrderInternalSubscription_getOrderInternal } from "graphql/types/getOrderInternalSubscription";
import { getPaymentMethodsV3_getPaymentMethodsV3_all_payment_modes_payment_methods as PAYMENT_METHODS } from "graphql/types/getPaymentMethodsV3";
import { clientRoutes } from "helpers/clientRoutes";
import { getUrlParam, isBrowser } from "helpers/commonHelpers";

const CARD_TYPE = [
  "MASTERCARD",
  "VISA",
  "MAESTRO",
  "AMEX",
  "DINERS",
  "DISCOVER",
  "JCB",
  "RUPAY",
];

enum OUTAGE_STATUS {
  DOWN = "DOWN",
  FLUCTUATE = "FLUCTUATE",
}

export enum JuspayPaymentType {
  Subscription = "subscription",
  Consult = "consult",
  Diagnostic = "diagnostic",
}

interface PaymentMethodOutageStatus {
  isDown: boolean;
  isFluctuate: boolean;
}

type OrderInfo = {
  internalOrderDetails: InternalOrder;
  category: string;
  typeFromRouteQuery: string;
};

export const returnCardTypeImage = (cardType: string) => {
  return CARD_TYPE.includes(cardType)
    ? `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/cards/${cardType}.svg`
    : `${process.env.NEXT_PUBLIC_247_CDN_URL}/images/cards/NEW_CARD.svg`;
};

export const getPaymentMethodOutageStatus = (
  status: string
): PaymentMethodOutageStatus => {
  return {
    isDown: status === OUTAGE_STATUS.DOWN,
    isFluctuate: status === OUTAGE_STATUS.FLUCTUATE,
  };
};

export const getPaymentMethodOutageStatusFromOutageList = (
  paymentMethods: PAYMENT_METHODS[]
): PaymentMethodOutageStatus => {
  let isDown = false,
    isFluctuate = false;
  paymentMethods.forEach((method) => {
    const outageStatus = method?.outage_list?.[0]?.outage_status;
    if (getPaymentMethodOutageStatus(outageStatus)?.isDown) {
      isDown = true;
    } else if (getPaymentMethodOutageStatus(outageStatus)?.isFluctuate) {
      isFluctuate = true;
    }
  });

  return { isDown, isFluctuate };
};

export const flatten = (arr) =>
  arr.reduce((a, b) => a.concat(Array.isArray(b) ? flatten(b) : b), []);

export const isTeamsSSO = () => {
  const source: string =
    getUrlParam("utm_source") || sessionStorage.getItem("utm_source") || "";
  return source === "MSTeams";
};

export enum UI_PAYMENT_STATUS {
  SUCCESS = "success",
  FAILURE = "failed",
  PENDING = "pending",
  ABORTED = "aborted",
  UNKNOWN = "unknown",
  REFUND = "refund",
  COD = "cod",
}

export enum PAYMENT_RETRY_LEVER {
  ON = "ON",
  OFF = "OFF",
  FLAT = "FLAT",
}

export enum LOCATION_SELECTION_MODE {
  AUTO_DETECT = "Auto Detect",
  MANUAL_ENTRY = "Manual entry",
}

export const formatPrice = (price: number): string => {
  if (price === Math.floor(price)) {
    return price.toString();
  }
  return Number(price).toFixed(2);
};

export const modeExpandPaymentTracking = (
  expanded: Boolean,
  modeName: string
) => {
  if (!expanded) {
    clevertapTracking("Payment mode expand", {
      modeName,
    });
  }
};

export const isJuspayErrorCode = (code: string) => code?.startsWith("JP70");

export const createReturnUrl = (orderInfo: OrderInfo): string => {
  const { internalOrderDetails, category, typeFromRouteQuery } = orderInfo;
  const allReturnUrls = {
    [JuspayPaymentType.Diagnostic]: `${clientRoutes.orderSucceedTest()}?order_id=${
      internalOrderDetails?.payment_order_id
    }`,
    [JuspayPaymentType.Consult]:
      //type guard for consult
      internalOrderDetails.type === JuspayPaymentType.Consult &&
      `${clientRoutes.appointments()}?apptid=${
        internalOrderDetails?.AppointmentDetails?.[0]?.id
      }&order_id=${internalOrderDetails?.payment_order_id}`,
    [JuspayPaymentType.Subscription]:
      typeFromRouteQuery === "onetap"
        ? clientRoutes.oneTapSuccess()
        : typeFromRouteQuery === "care"
        ? clientRoutes.myMembership()
        : clientRoutes.careLanding(),
  };
  return allReturnUrls[category] || "/";
};

export const orderPageUrl = (
  returnUrl: string,
  orderStatus: string,
  payment_order_id: string
) =>
  `${returnUrl}${
    returnUrl.includes("?") ? "&" : "?"
  }status=${orderStatus}&order_id=${payment_order_id}`;

export const enableRetryOrderCOD = ({
  isPaymentLinkOrder,
  pendingPageEnabled,
  isCodDisabledForRetry,
}: {
  isPaymentLinkOrder: boolean;
  pendingPageEnabled: boolean;
  isCodDisabledForRetry: boolean;
}) => {
  if (isPaymentLinkOrder || pendingPageEnabled || isCodDisabledForRetry)
    return false;
  return (
    process.env.NEXT_PUBLIC_DIAGNOSTIC_RETRY_COD_ENABLED === "true" &&
    isBrowser() &&
    sessionStorage.getItem("COD_failed") === null
  );
};
export type InternalOrder =
  | (getOrderInternalDiagnostic_getOrderInternal & {
      type: JuspayPaymentType.Diagnostic;
    })
  | (getOrderInternalSubscription_getOrderInternal & {
      type: JuspayPaymentType.Subscription;
    })
  | (getOrderInternalConsult_getOrderInternal & {
      type: JuspayPaymentType.Consult;
    });

export const getVerticalOrderDetails = (
  internalOrderDetails: InternalOrder
) => {
  if (internalOrderDetails?.type === JuspayPaymentType.Consult)
    return internalOrderDetails?.AppointmentDetails?.[0];
  if (internalOrderDetails?.type === JuspayPaymentType.Diagnostic)
    return internalOrderDetails?.DiagnosticsPaymentDetails?.ordersList;
  return internalOrderDetails?.SubscriptionOrderDetails;
};

export const LOB = {
  [JuspayPaymentType.Diagnostic]: "lab-tests",
  [JuspayPaymentType.Consult]: "specialties",
  [JuspayPaymentType.Subscription]: "circle",
};
